import React, { useMemo } from 'react';
import { GridColumn, RowAction } from '../../../types/datagrid';
import { GridRow } from './GridRow';

interface GridBodyProps {
  data: any[];
  columns: GridColumn[];
  loading?: boolean;
  selectedRows?: string[];
  onRowSelect?: (rowIds: string[]) => void;
  onRowClick?: (row: any, index: number) => void;
  onRowDoubleClick?: (row: any, index: number) => void;
  onCellEdit?: (rowId: string, columnId: string, value: any) => Promise<void>;
  editingCell?: {
    rowId: string;
    columnId: string;
    value: any;
  };
  rowHeight?: number;
  enableVirtualization?: boolean;
  compactDensity?: boolean;
  permissions?: {
    canEdit?: boolean;
    canDelete?: boolean;
  };
  rowActions?: RowAction[];
  onRowAction?: (rowId: string, action: string, rowData: any) => void;
}

export const GridBody: React.FC<GridBodyProps> = ({
  data,
  columns,
  loading = false,
  selectedRows = [],
  onRowSelect,
  onRowClick,
  onRowDoubleClick,
  onCellEdit,
  editingCell,
  rowHeight = 44,
  enableVirtualization: _enableVirtualization = false,
  compactDensity = true,
  permissions = {},
  rowActions = [],
  onRowAction,
}) => {
  // Memoize visible columns to avoid recalculation
  const visibleColumns = useMemo(() => {
    return columns.filter(col => col.isVisible);
  }, [columns]);

  // Handle row selection
  const handleRowSelect = (rowId: string, selected: boolean) => {
    if (!onRowSelect) return;

    if (selected) {
      onRowSelect([...selectedRows, rowId]);
    } else {
      onRowSelect(selectedRows.filter(id => id !== rowId));
    }
  };

  // Handle select all
  const handleSelectAll = (selected: boolean) => {
    if (!onRowSelect) return;

    if (selected) {
      const allRowIds = data.map(row => row.id || row._id).filter(Boolean);
      onRowSelect(allRowIds);
    } else {
      onRowSelect([]);
    }
  };

  // Check if all rows are selected
  const allRowsSelected = useMemo(() => {
    const dataRowIds = data.map(row => row.id || row._id).filter(Boolean);
    return dataRowIds.length > 0 && dataRowIds.every(id => selectedRows.includes(id));
  }, [data, selectedRows]);

  // Check if some rows are selected
  const someRowsSelected = useMemo(() => {
    return selectedRows.length > 0 && !allRowsSelected;
  }, [selectedRows.length, allRowsSelected]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-3 text-gray-500">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-brand-600"></div>
          <span className="text-sm">Loading...</span>
        </div>
      </div>
    );
  }

  // Empty state
  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center text-gray-500">
          <div className="mb-2">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">No data found</h3>
          <p className="text-xs text-gray-500">Try adjusting your search or filter criteria</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex-1 overflow-auto"
      role="grid"
      aria-rowcount={data.length}
      aria-colcount={visibleColumns.length}
    >
      {/* Selection Header Row (if row selection is enabled) */}
      {onRowSelect && (
        <div
          className="flex items-center bg-gray-50 border-b border-gray-200 px-3"
          style={{ height: Math.max(32, rowHeight - 12) }}
        >
          <input
            type="checkbox"
            checked={allRowsSelected}
            ref={input => {
              if (input) input.indeterminate = someRowsSelected;
            }}
            onChange={e => handleSelectAll(e.target.checked)}
            className="h-4 w-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
            aria-label="Select all rows"
          />
          <span className="ml-3 text-xs text-gray-500">
            {selectedRows.length > 0 ? `${selectedRows.length} selected` : 'Select all'}
          </span>
        </div>
      )}

      {/* Data Rows */}
      <div className="divide-y divide-gray-200">
        {data.map((row, index) => {
          const rowId = row.id || row._id || index.toString();
          const isSelected = selectedRows.includes(rowId);
          const isEditing = editingCell?.rowId === rowId;

          return (
            <GridRow
              key={rowId}
              row={row}
              columns={visibleColumns}
              index={index}
              rowId={rowId}
              isSelected={isSelected}
              isEditing={isEditing}
              editingCell={editingCell}
              height={rowHeight}
              compactDensity={compactDensity}
              onSelect={onRowSelect ? selected => handleRowSelect(rowId, selected) : undefined}
              onClick={onRowClick ? () => onRowClick(row, index) : undefined}
              onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(row, index) : undefined}
              onCellEdit={onCellEdit}
              permissions={permissions}
              rowActions={rowActions}
              onRowAction={onRowAction}
            />
          );
        })}
      </div>
    </div>
  );
};
